/**
 * Admin → Affiliates → Applications (PRP-MCP-11 G5, founder decision F3).
 *
 * The anterotrail.com/affiliates form submissions, newest first: name, email,
 * how they'll share it, and the terms they accepted ("Accepted terms v1 at
 * <time>"). "Create affiliate" hands the application to the page's create form
 * (pre-filled; the affiliate then carries the accepted version and time). An
 * application that already became an affiliate links to it. `highlightId` (the
 * founder email's `?application=<id>`) marks that row.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { AdminAffiliatesAPI, affiliateErrText, termsText } from '../../lib/adminAffiliatesApi';
import type { AffiliateApplication } from '../../lib/adminAffiliatesApi';

interface Props {
  onPick: (app: AffiliateApplication) => void;
  highlightId?: string | null;
  /** Bumped by the page after an affiliate is created, so the list re-reads. */
  reloadKey?: number;
}

const AffiliateApplications: React.FC<Props> = ({ onPick, highlightId, reloadKey = 0 }) => {
  const [rows, setRows] = useState<AffiliateApplication[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      setRows((await AdminAffiliatesAPI.applications()).applications);
    } catch (err) {
      setError(affiliateErrText(err, 'Could not load applications.'));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load, reloadKey]);

  return (
    <section className="space-y-2 rounded-md border border-border p-4" data-testid="affiliate-applications">
      <h2 className="text-base font-semibold">Applications{rows ? ` (${rows.length})` : ''}</h2>
      {error && <p className="text-sm font-medium text-red-500">{error}</p>}
      {rows === null && !error && <p className="text-sm text-muted-foreground">Loading…</p>}
      {rows && rows.length === 0 && <p className="text-sm text-muted-foreground">No applications yet.</p>}
      {rows && rows.length > 0 && (
        <table className="w-full text-sm">
          <thead className="text-left text-xs text-muted-foreground">
            <tr>
              <th className="p-1">Name</th><th className="p-1">Email</th><th className="p-1">How they&apos;ll share it</th>
              <th className="p-1">Terms</th><th className="p-1" />
            </tr>
          </thead>
          <tbody>
            {rows.map((a) => (
              <tr key={a.id} data-application-id={a.id}
                className={`border-t border-border align-top${a.id === highlightId ? ' bg-yellow-50 dark:bg-yellow-900/20' : ''}`}>
                <td className="p-1">{a.name}</td>
                <td className="p-1 break-all">{a.email}</td>
                <td className="p-1 whitespace-pre-wrap">{a.how}</td>
                <td className="p-1">{termsText(a.terms_version, a.terms_accepted_at)}</td>
                <td className="p-1 text-right">
                  {a.affiliate_id ? (
                    <Link to={`/admin/affiliates/${a.affiliate_id}`} className="text-xs underline">Affiliate created</Link>
                  ) : (
                    <button type="button" className="rounded-md border border-border px-2 py-0.5 text-xs"
                      onClick={() => onPick(a)}>
                      Create affiliate
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
};

export default AffiliateApplications;
